import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeHeader from "./Headesr";
import NewHomepageFooter from "./NewHomepageFooter";
import { Helmet, HelmetProvider } from "react-helmet-async";
import "./NewsEvents.css";
import "./MarketingRebrand.css";

const PUBLIC_SITE_URL = "https://smart-lend.com";
const DEFAULT_NEWS_TITLE = "News & Events | SmartLend";
const DEFAULT_NEWS_DESCRIPTION = "Latest SmartLend news, events and updates.";
const ARTICLE_META_OVERRIDES = {
  "the-photo-shoot-that-wasn-t": {
    title: "Singapore Property Loan Fraud Case: The Photo Shoot That Wasn't | SmartLend",
    description:
      "SmartLend examines a Singapore property loan identity fraud case involving a forged NRIC, a Katong home, and a nearly approved $2.9M loan.",
  },
};

const getArticleUrl = (slug) => `${PUBLIC_SITE_URL}/news-events/${slug}/`;
const getListingUrl = () => `${PUBLIC_SITE_URL}/news-events/`;

const NewsEvents = () => {
  const history = useHistory();

  const [pageLoad, setPageLoad] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const [newsandevents, setnewsandevents] = useState([]);
  const [activeNews, setActiveNews] = useState(null);
  const [newsIsOpen, setNewsIsOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("Newsletter"); // "News" | "Events" | "Newsletter"
  const [slug, setSlug] = useState(null);

  useEffect(() => setPageLoad(true), []);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html || "", "text/html");
    return doc.body.textContent || "";
  };

  const looksTruncated = (value) => {
    const text = stripHtml(value);
    if (!text) return true;
    if (text.length < 80) return true;
    if (/\s[a-zA-Z]{1,2}$/.test(text)) return true;
    if (/[,;:]$/.test(text)) return true;
    return false;
  };

  const trimDescription = (value, maxLength = 160) => {
    const text = stripHtml(value);
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 1).replace(/\s+\S*$/, "")}...`;
  };

  const removeHTMLTags = (html) => stripHtml(html);

  const slugify = (text) =>
    String(text || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const getArticleSlug = (item) => item?.slug || slugify(item?.content_title);

  const getSeoTitle = (article) => {
    const override = ARTICLE_META_OVERRIDES[article?.slug];
    return override?.title || article?.seo_title || article?.title || DEFAULT_NEWS_TITLE;
  };

  const getSeoDescription = (article) => {
    if (!article) return DEFAULT_NEWS_DESCRIPTION;

    const override = ARTICLE_META_OVERRIDES[article.slug];
    if (override?.description) return override.description;

    const candidates = [
      article.seo_description,
      article.og_description,
      article.excerpt,
      trimDescription(article.content),
    ];

    return (
      candidates.find((candidate) => candidate && !looksTruncated(candidate)) ||
      trimDescription(article.content)
    );
  };

  const getOgTitle = (article) =>
    ARTICLE_META_OVERRIDES[article?.slug]?.title ||
    article?.og_title ||
    getSeoTitle(article);

  const getOgDescription = (article) =>
    ARTICLE_META_OVERRIDES[article?.slug]?.description ||
    article?.og_description ||
    getSeoDescription(article);

  const formatDate = (inputDate) => {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const estimateReadingTime = (html) => {
    const text = stripHtml(html);
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(1, Math.round(words / 200));
    return `${minutes} min read`;
  };

  const getAllNews = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/news_and_events/getAll",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );

      if (response.status === 200) {
        const json = await response.json();
        setnewsandevents(Array.isArray(json.data) ? json.data : []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getAllNews();
  }, []);

  // Read slug from URL on initial load
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/^\/news-events\/(.+)$/);
    if (match) {
      setSlug(decodeURIComponent(match[1]).replace(/\/+$/, ""));
    } else {
      setSlug(null);
    }
  }, []);

  // Open article when slug + data exist
  useEffect(() => {
    if (!slug || newsandevents.length === 0) return;

    const matched = newsandevents.find((n) => getArticleSlug(n) === slug);

    if (matched) {
      setActiveNews({
        id: matched.id,
        title: matched.content_title,
        slug: getArticleSlug(matched),
        content: matched.content,
        excerpt: matched.excerpt,
        date: matched.publish_date,
        thumbnail: matched.thumbnail,
        type: matched.type_of_content,
        seo_title: matched.seo_title,
        seo_description: matched.seo_description,
        seo_keywords: matched.seo_keywords,
        canonical_url: matched.canonical_url,
        og_title: matched.og_title,
        og_description: matched.og_description,
        og_image: matched.og_image,
        meta_robots: matched.meta_robots,
      });
      setNewsIsOpen(true);
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [slug, newsandevents]);

  const sortedActiveItems = useMemo(() => {
    const filtered = newsandevents
      .filter((x) => x && x.status)
      .filter((x) => x.type_of_content === activeTab);

    const q = searchQuery.trim().toLowerCase();
    const searched = !q
      ? filtered
      : filtered.filter((x) => {
          const title = String(x.content_title || "").toLowerCase();
          const body = stripHtml(x.content).toLowerCase();
          return title.includes(q) || body.includes(q);
        });

    return searched.sort(
      (a, b) => new Date(b.publish_date) - new Date(a.publish_date)
    );
  }, [newsandevents, activeTab, searchQuery]);

  const openNews = (item) => {
    const itemSlug = getArticleSlug(item);
    setActiveNews({
      id: item.id,
      title: item.content_title,
      slug: itemSlug,
      content: item.content,
      excerpt: item.excerpt,
      date: item.publish_date,
      thumbnail: item.thumbnail,
      type: item.type_of_content,
      seo_title: item.seo_title,
      seo_description: item.seo_description,
      seo_keywords: item.seo_keywords,
      canonical_url: item.canonical_url,
      og_title: item.og_title,
      og_description: item.og_description,
      og_image: item.og_image,
      meta_robots: item.meta_robots,
    });
    setNewsIsOpen(true);
    window.history.pushState({}, "", `/news-events/${itemSlug}/`);
    setSlug(itemSlug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeNews = () => {
    setActiveNews(null);
    setNewsIsOpen(false);
    window.history.pushState({}, "", `/news-events/`);
    setSlug(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const getRecommendations = (current, all, limit = 6) => {
    if (!current) return [];

    // same category first (News/Events/Newsletter), exclude current
    const sameType = all
      .filter((x) => x && x.status)
      .filter((x) => x.id !== current.id)
      .filter((x) => x.type_of_content === current.type)
      .sort((a, b) => new Date(b.publish_date) - new Date(a.publish_date));

    // fallback pool: anything else (still exclude current)
    const other = all
      .filter((x) => x && x.status)
      .filter((x) => x.id !== current.id)
      .sort((a, b) => new Date(b.publish_date) - new Date(a.publish_date));

    // If sameType is not enough, fill from other (dedupe by id)
    const picked = [];
    const pushUnique = (arr) => {
      for (const item of arr) {
        if (picked.length >= limit) break;
        if (!picked.some((p) => p.id === item.id)) picked.push(item);
      }
    };

    pushUnique(sameType);
    pushUnique(other);

    return picked.slice(0, limit);
  };

  const recommendations = useMemo(() => {
    if (!activeNews) return [];
    return getRecommendations(activeNews, newsandevents, 6);
  }, [activeNews, newsandevents]);

  const articleStructuredData = useMemo(() => {
    if (!activeNews) return null;

    const description = getSeoDescription(activeNews);
    const url = getArticleUrl(activeNews.slug);

    return {
      "@context": "https://schema.org",
      "@type": activeNews.type === "Events" ? "Event" : "NewsArticle",
      headline: getSeoTitle(activeNews),
      description,
      image: activeNews.og_image || activeNews.thumbnail || undefined,
      datePublished: activeNews.date,
      dateModified: activeNews.date,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": url,
      },
      author: {
        "@type": "Organization",
        name: "SmartLend",
      },
      publisher: {
        "@type": "Organization",
        name: "SmartLend",
        logo: {
          "@type": "ImageObject",
          url: `${PUBLIC_SITE_URL}/logo512.png`,
        },
      },
    };
  }, [activeNews]);

  const activeTitle = activeNews ? getSeoTitle(activeNews) : DEFAULT_NEWS_TITLE;
  const activeDescription = activeNews
    ? getSeoDescription(activeNews)
    : DEFAULT_NEWS_DESCRIPTION;
  const activeCanonical = activeNews ? getArticleUrl(activeNews.slug) : getListingUrl();
  const activeOgTitle = activeNews ? getOgTitle(activeNews) : DEFAULT_NEWS_TITLE;
  const activeOgDescription = activeNews
    ? getOgDescription(activeNews)
    : DEFAULT_NEWS_DESCRIPTION;

  return (
    <div className="nePage slMarketingPage" style={{ overflowX: "hidden" }}>
      <HelmetProvider>
        <Helmet>
          <title>{activeTitle}</title>
          <meta
            name="description"
            content={activeDescription}
          />
          <meta
            name="keywords"
            content={activeNews ? activeNews.seo_keywords || "" : ""}
          />
          <meta
            name="robots"
            content={activeNews ? activeNews.meta_robots || "index,follow" : "index,follow"}
          />
          <link
            rel="canonical"
            href={activeCanonical}
          />
          <meta property="og:type" content={activeNews ? "article" : "website"} />
          <meta
            property="og:title"
            content={activeOgTitle}
          />
          <meta
            property="og:description"
            content={activeOgDescription}
          />
          <meta
            property="og:image"
            content={activeNews ? activeNews.og_image || activeNews.thumbnail || "" : ""}
          />
          <meta
            property="og:url"
            content={activeCanonical}
          />
          {activeNews ? (
            <meta property="article:published_time" content={activeNews.date || ""} />
          ) : null}
          {activeNews ? (
            <meta property="article:modified_time" content={activeNews.date || ""} />
          ) : null}
          {activeNews ? <meta name="twitter:card" content="summary_large_image" /> : null}
          {activeNews ? (
            <meta
              name="twitter:title"
              content={activeOgTitle}
            />
          ) : null}
          {activeNews ? (
            <meta
              name="twitter:description"
              content={activeOgDescription}
            />
          ) : null}
          {activeNews && (activeNews.og_image || activeNews.thumbnail) ? (
            <meta name="twitter:image" content={activeNews.og_image || activeNews.thumbnail} />
          ) : null}
          {articleStructuredData ? (
            <script type="application/ld+json">
              {JSON.stringify(articleStructuredData)}
            </script>
          ) : null}
        </Helmet>
        <HomeHeader />

        {/* Body */}
        {newsIsOpen && activeNews ? (
          <div className="neShell">
            <div
              className="neArticleLayout"
              style={{
                opacity: pageLoad ? 1 : 0,
                transform: pageLoad ? "translateY(0px)" : "translateY(10px)",
                transition: "all 0.6s ease",
              }}
            >
              {/* MAIN ARTICLE */}
              <div className="neArticleMain">
                <div className="neBack" onClick={closeNews}>
                  <ArrowBackIcon style={{ fontSize: 18 }} />
                  Back to stories
                </div>

                {activeNews.thumbnail ? (
                  <img
                    className="neHero"
                    src={activeNews.thumbnail}
                    alt={activeNews.title}
                  />
                ) : null}

                <h1 className="neArticleTitle">{activeNews.title}</h1>

                <div className="neArticleMeta">
                  <span className="neByline">SmartLend</span>
                  <span className="neDot">•</span>
                  <span>{formatDate(activeNews.date)}</span>
                  <span className="neDot">•</span>
                  <span>{estimateReadingTime(activeNews.content)}</span>
                  <span className="neDot">•</span>
                  <span className="nePill">{activeNews.type}</span>
                </div>

                <div
                  className="neContent"
                  dangerouslySetInnerHTML={{ __html: activeNews.content }}
                />
              </div>

              {/* RECOMMENDATIONS */}
              <aside className="neAside">
                <div className="neAsideTitle">More from SmartLend</div>

                {recommendations.length === 0 ? (
                  <div
                    style={{
                      fontFamily: "var(--ne-sans)",
                      color: "var(--ne-subtext)",
                      fontSize: 13,
                    }}
                  >
                    No recommendations yet.
                  </div>
                ) : (
                  recommendations.map((item) => (
                    <div
                      key={item.id}
                      className="neRecItem"
                      onClick={() => openNews(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") openNews(item);
                      }}
                    >
                      <div>
                        <div className="neRecTitle">{item.content_title}</div>
                        <div className="neRecMeta">
                          <span>{formatDate(item.publish_date)}</span>
                          <span className="neDot">•</span>
                          <span>{estimateReadingTime(item.content)}</span>
                        </div>
                      </div>

                      {item.thumbnail ? (
                        <img
                          className="neRecThumb"
                          src={item.thumbnail}
                          alt={item.content_title}
                        />
                      ) : (
                        <div className="neRecThumb" />
                      )}
                    </div>
                  ))
                )}
              </aside>
            </div>
          </div>
        ) : (
          <div className="neShell">
            <div
              className="neContainer"
              style={{
                opacity: pageLoad ? 1 : 0,
                transform: pageLoad ? "translateY(0px)" : "translateY(10px)",
                transition: "all 0.6s ease",
              }}
            >
              <div className="neHeaderRow">
                <div>
                  <div className="slEyebrow"><span className="slEyebrowDot" />News & Events</div>
                  <h1 className="neTitle">News & Events</h1>
                  <p className="neSubtitle">
                    Read SmartLend updates, lender workflow notes, market explainers,
                    and event announcements in one clean listing.
                  </p>
                </div>

                <div className="neControls">
                  <div
                    className="neSearch"
                    style={{ width: screenWidth <= 760 ? "100%" : undefined }}
                  >
                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search stories…"
                    />
                  </div>
                </div>
              </div>

              <div className="neTabs">
                {["Newsletter", "Events", "News"].map((t) => (
                  <button
                    key={t}
                    className={`neTab ${activeTab === t ? "neTabActive" : ""}`}
                    onClick={() => setActiveTab(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <div className="neList">
                {sortedActiveItems.length === 0 ? (
                  <div
                    style={{
                      fontFamily: "var(--ne-sans)",
                      color: "var(--ne-subtext)",
                      padding: "18px 0",
                    }}
                  >
                    No stories found.
                  </div>
                ) : (
                  sortedActiveItems.map((item) => (
                    <div
                      key={item.id}
                      className="neRow"
                      onClick={() => openNews(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") openNews(item);
                      }}
                    >
                      <div>
                        <h2 className="neRowTitle">{item.content_title}</h2>

                        <p className="neRowExcerpt">
                          {(() => {
                            const text =
                              item.excerpt || removeHTMLTags(item.content || "");
                            const max = screenWidth <= 760 ? 140 : 200;
                            return text.length > max
                              ? text.slice(0, max) + "…"
                              : text;
                          })()}
                        </p>

                        <div className="neMetaRow">
                          <span className="nePill">{item.type_of_content}</span>
                          <span className="neDot">•</span>
                          <span>{formatDate(item.publish_date)}</span>
                          <span className="neDot">•</span>
                          <span>{estimateReadingTime(item.content)}</span>
                        </div>
                      </div>

                      {item.thumbnail ? (
                        <img
                          className="neThumb"
                          src={item.thumbnail}
                          alt={item.content_title}
                        />
                      ) : (
                        <div className="neThumb" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </HelmetProvider>
      <NewHomepageFooter />
    </div>
  );
};

export default NewsEvents;
